import React from 'react';
import { Box } from '@mui/material';
import { LEGAL_DOCS } from './legalContent';

const ff = "'Poppins', sans-serif";

// Map a URL slug to its document.
const BY_SLUG = Object.values(LEGAL_DOCS).reduce((acc, d) => {
  acc[d.slug] = d;
  return acc;
}, {});

// A short line with no sentence-ending punctuation reads as a section heading.
const isHeading = (line) => {
  if (/^[*\-•]\s/.test(line)) return false;
  if (line.length > 72) return false;
  if (/[.:;]$/.test(line)) return false;
  return true;
};
const isBullet = (line) => /^[*\-•]\s/.test(line);

const formatUpdated = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export default function LegalPage({ slug }) {
  const doc = BY_SLUG[slug];

  if (!doc) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: ff, color: '#718096' }}>
        Page not found.
      </Box>
    );
  }

  const updated = formatUpdated(doc.updated);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)', py: { xs: 2, sm: 5 }, px: 2 }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 760,
          mx: 'auto',
          background: '#FFF',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(50, 166, 216, 0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Branded header */}
        <Box sx={{ background: 'linear-gradient(135deg, #32A6D8 0%, #2B8FB8 100%)', color: '#FFF', textAlign: 'center', py: 4, px: 3 }}>
          <Box sx={{ fontFamily: ff, fontSize: 26, fontWeight: 700, letterSpacing: 0.5 }}>OkTreat</Box>
          <Box sx={{ fontFamily: ff, fontSize: 18, fontWeight: 600, mt: 1.5 }}>{doc.title}</Box>
          {updated && (
            <Box sx={{ fontFamily: ff, fontSize: 12, opacity: 0.9, mt: 0.75 }}>Last updated {updated}</Box>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ p: { xs: 3, sm: 5 } }}>
          {doc.body.map((line, i) => {
            if (isBullet(line)) {
              return (
                <Box key={i} sx={{ fontFamily: ff, fontSize: 14.5, lineHeight: 1.7, color: '#4A5568', pl: 2.5, position: 'relative', mb: 0.75 }}>
                  <Box component="span" sx={{ position: 'absolute', left: 6, color: '#32A6D8' }}>•</Box>
                  {line.replace(/^[*\-•]\s/, '')}
                </Box>
              );
            }
            if (isHeading(line)) {
              return (
                <Box key={i} sx={{ fontFamily: ff, fontSize: 16, fontWeight: 700, color: '#1A202C', mt: i === 0 ? 0 : 3.5, mb: 1 }}>
                  {line}
                </Box>
              );
            }
            return (
              <Box key={i} sx={{ fontFamily: ff, fontSize: 14.5, lineHeight: 1.75, color: '#4A5568', mb: 1.5 }}>
                {line}
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ borderTop: '1px solid #EDF2F7', textAlign: 'center', py: 2.5, px: 3 }}>
          <Box sx={{ fontFamily: ff, fontSize: 12, color: '#A0AEC0' }}>
            © {new Date().getFullYear()} OkTreat · Pet sitting community
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
